import { CheckoutComponentMetric } from '@app/pages/checkout-app/services/extensions/extensions.service';
import { ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

const upsellDataInterceptor = (metricsData: CheckoutComponentMetric[]): CheckoutComponentMetric[] => {
  const impressions = metricsData.find((metric) => metric.METRIC_NAME === 'number_unique_impressions');
  const revenue = metricsData.find((metric) => metric.METRIC_NAME === 'revenue');
  const itemsAdded = metricsData.find((metric) => metric.METRIC_NAME === 'number_items_added');
  const attributedOrders = metricsData.find((metric) => metric.METRIC_NAME === 'attributed_orders');
  const snapshotDate = impressions?.LATEST_SNAPSHOT_DATE ?? null;

  const impressionsValue = impressions?.METRIC_TOTAL_VALUE ?? 0;
  const revenueValue = revenue?.METRIC_TOTAL_VALUE ?? 0;
  const itemsAddedValue = itemsAdded?.METRIC_TOTAL_VALUE ?? 0;
  const attributedOrdersValue = attributedOrders?.METRIC_TOTAL_VALUE ?? 0;

  return [
    {
      METRIC_NAME: 'number_unique_impressions',
      METRIC_TOTAL_VALUE: impressionsValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'revenue',
      METRIC_TOTAL_VALUE: revenueValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'revenue_per_order',
      METRIC_TOTAL_VALUE: attributedOrdersValue > 0 ? revenueValue / attributedOrdersValue : 0,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'number_items_added',
      METRIC_TOTAL_VALUE: itemsAddedValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
  ];
};

const orderProtectionDataInterceptor = (metricsData: CheckoutComponentMetric[]): CheckoutComponentMetric[] => {
  const impressions = metricsData.find((metric) => metric.METRIC_NAME === 'number_unique_impressions');
  const attributedOrders = metricsData.find((metric) => metric.METRIC_NAME === 'attributed_orders');
  const revenue = metricsData.find((metric) => metric.METRIC_NAME === 'revenue');
  const snapshotDate = impressions?.LATEST_SNAPSHOT_DATE ?? null;

  const impressionsValue = impressions?.METRIC_TOTAL_VALUE ?? 0;
  const ordersValue = attributedOrders?.METRIC_TOTAL_VALUE ?? 0;
  const revenueValue = revenue?.METRIC_TOTAL_VALUE ?? 0;

  return [
    {
      METRIC_NAME: 'number_unique_impressions',
      METRIC_TOTAL_VALUE: impressionsValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'attributed_orders',
      METRIC_TOTAL_VALUE: ordersValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'usage_rate',
      METRIC_TOTAL_VALUE: impressionsValue > 0 ? (ordersValue / impressionsValue) * 100 : 0,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
    {
      METRIC_NAME: 'revenue',
      METRIC_TOTAL_VALUE: revenueValue,
      LATEST_SNAPSHOT_DATE: snapshotDate,
    },
  ];
};

export const ExtComponentTypesOptions = [
  {
    label: 'Digital upsell',
    value: ExtensionComponentType.CUSTOM_UPSELL,
    disabled: false,
    dataInterceptor: upsellDataInterceptor,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
        tooltip: 'Number of times this extension was shown to shoppers',
      },
      {
        title: 'Revenue',
        field: 'revenue',
        displayCurrency: true,
        tooltip: 'Total revenue attributed to items added via this upsell',
      },
      {
        title: 'Revenue Per Order',
        field: 'revenue_per_order',
        displayCurrency: true,
        tooltip: 'Average upsell revenue per order that included an upsell item (Revenue ÷ orders with an added item)',
      },
      {
        title: '# of items purchased',
        field: 'number_items_added',
        tooltip: 'Total number of upsell items added to orders',
      },
    ],
  },
  {
    label: 'Delivery date picker',
    value: ExtensionComponentType.DATE_PICKER,
    disabled: false,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: '# of orders with a non-default date',
        field: 'number_dates_selected',
      },
      {
        title: 'Avg # of calendar days ahead',
        field: 'number_days_ahead_avg',
      },
    ],
  },
  {
    label: 'Cart Control',
    value: ExtensionComponentType.CART_CONTROL,
    disabled: false,
    metrics: [
      {
        title: '# of items added',
        field: 'number_item_added',
      },
      {
        title: '# of items removed',
        field: 'number_item_removed',
      },
      {
        title: '# of subscription upgrades',
        field: 'subscriptions_added',
      },
    ],
  },
  {
    label: 'Delivery Countdown Timer',
    value: ExtensionComponentType.DELIVERY_COUNTDOWN_TIMER,
    disabled: false,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: '# of orders with timer method',
        field: 'orders_with_timer_method',
      },
      {
        title: 'Conversion rate with promoted method',
        field: 'conversion_rate_with_promoted_method',
      },
      {
        title: 'Conversion rate without promoted method',
        field: 'conversion_rate_without_promoted_method',
      },
    ],
  },
  {
    label: 'Checkout content',
    value: ExtensionComponentType.CHECKOUT_CONTENT,
    disabled: false,
  },
  {
    label: 'Upsell',
    value: ExtensionComponentType.CHECKOUT_UPSELL,
    disabled: false,
    dataInterceptor: upsellDataInterceptor,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
        tooltip: 'Number of times this extension was shown to shoppers',
      },
      {
        title: 'Revenue',
        field: 'revenue',
        displayCurrency: true,
        tooltip: 'Total revenue attributed to items added via this upsell',
      },
      {
        title: 'Revenue Per Order',
        field: 'revenue_per_order',
        displayCurrency: true,
        tooltip: 'Average upsell revenue per order that included an upsell item (Revenue ÷ orders with an added item)',
      },
      {
        title: '# of items purchased',
        field: 'number_items_added',
        tooltip: 'Total number of upsell items added to orders',
      },
      /** Not supported yet */
      // {
      //   title: '# of checkouts raised above threshold',
      //   field: 'number_raised_above_threshold',
      // },
    ],
  },
  {
    label: 'Gift unlock',
    value: ExtensionComponentType.GIFT_UNLOCK,
    disabled: false,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: '# of items added',
        field: 'number_items_added',
      },
      {
        title: '# of orders with gift added after info',
        field: 'number_opened_gifts_after_contact_information',
      },
    ],
  },
  {
    label: 'Gift note',
    value: ExtensionComponentType.GIFT_NOTE,
    disabled: false,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: '# of gift orders',
        field: 'number_gift_orders',
      },
      {
        title: '% of orders as gifts',
        field: 'percentage_gift_notes',
      },
    ],
  },
  {
    label: 'Shipping rates filter',
    value: ExtensionComponentType.SHIPPING_RATES_FILTER,
    disabled: false,
    requiresShopifyDeliveryOptionsFunction: true,
    dataInterceptor: (metricsData: CheckoutComponentMetric[]): CheckoutComponentMetric[] => {
      const impressions = metricsData.find((metric) => metric.METRIC_NAME === 'number_unique_impressions');
      const checkboxSelected = metricsData.find((metric) => metric.METRIC_NAME === 'is_checkbox_checked');
      const orderMatches = metricsData.find((metric) => metric.METRIC_NAME === 'orders_matches');
      const asrMatches = metricsData.find((metric) => metric.METRIC_NAME === 'asr_matches');
      const snapshotDate = impressions?.LATEST_SNAPSHOT_DATE ?? null;

      const impressionsValue = impressions?.METRIC_TOTAL_VALUE ?? 0;
      const checkboxValue = checkboxSelected?.METRIC_TOTAL_VALUE ?? 0;
      const orderMatchesValue = orderMatches?.METRIC_TOTAL_VALUE ?? 0;
      const asrMatchesValue = asrMatches?.METRIC_TOTAL_VALUE ?? 0;

      return [
        {
          METRIC_NAME: 'number_unique_impressions',
          METRIC_TOTAL_VALUE: impressionsValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'selection_rate',
          METRIC_TOTAL_VALUE: impressionsValue > 0 ? (checkboxValue / impressionsValue) * 100 : 0,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'revenue_impact',
          METRIC_TOTAL_VALUE: asrMatchesValue * orderMatchesValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
      ];
    },

    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: 'Checkbox selection %',
        field: 'selection_rate',
      },
      {
        title: 'Revenue impact',
        field: 'revenue_impact',
        displayCurrency: true,
      },
    ],
  },
  {
    label: 'Coupon code rejection',
    value: ExtensionComponentType.DISCOUNT_REJECTION_COUPON,
    disabled: false,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: '% Usage Rate',
        field: 'replacement_code_usage_number',
      },
      {
        title: 'Orders Recovered',
        field: 'orders_recovered',
      },
      {
        title: 'Revenue Recovered',
        field: 'revenue_recovered',
      },
    ],
  },
  {
    label: 'Product Upgrade',
    value: ExtensionComponentType.PRODUCT_UPGRADE,
    disabled: false,
    dataInterceptor: (metricsData: CheckoutComponentMetric[]): CheckoutComponentMetric[] => {
      const impressions = metricsData.find((m) => m.METRIC_NAME === 'number_unique_impressions');
      const revenue = metricsData.find((m) => m.METRIC_NAME === 'revenue_added');
      const upgradeOrders = metricsData.find((m) => m.METRIC_NAME === 'upgrade_orders');
      const upgradeCheckouts = metricsData.find((m) => m.METRIC_NAME === 'upgrade_checkouts');
      const subscriptionProductsAdded = metricsData.find((m) => m.METRIC_NAME === 'subscription_products_added');
      const snapshotDate = impressions?.LATEST_SNAPSHOT_DATE ?? null;

      const impressionsValue = impressions?.METRIC_TOTAL_VALUE ?? 0;
      const revenueValue = revenue?.METRIC_TOTAL_VALUE ?? 0;
      const upgradeOrdersValue = upgradeOrders?.METRIC_TOTAL_VALUE ?? 0;
      const upgradeCheckoutsValue = upgradeCheckouts?.METRIC_TOTAL_VALUE ?? 0;
      const subscriptionProductsAddedValue = subscriptionProductsAdded?.METRIC_TOTAL_VALUE ?? 0;

      return [
        {
          METRIC_NAME: 'number_unique_impressions',
          METRIC_TOTAL_VALUE: impressionsValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'revenue',
          METRIC_TOTAL_VALUE: revenueValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'avg_revenue_added',
          METRIC_TOTAL_VALUE: upgradeOrdersValue > 0 ? revenueValue / upgradeOrdersValue : 0,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'cvr',
          METRIC_TOTAL_VALUE: upgradeCheckoutsValue > 0 ? (upgradeOrdersValue / upgradeCheckoutsValue) * 100 : 0,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'subscription_products_added',
          METRIC_TOTAL_VALUE: subscriptionProductsAddedValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
      ];
    },
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: 'Revenue added',
        field: 'revenue',
        displayCurrency: true,
      },
      {
        title: 'AVG Revenue added',
        field: 'avg_revenue_added',
        displayCurrency: true,
      },
      {
        title: 'CVR %',
        field: 'cvr',
      },
      {
        title: 'Products Upgraded to Subscription',
        field: 'subscription_products_added',
      },
    ],
  },
  {
    label: 'Dynamic Reviews',
    value: ExtensionComponentType.DYNAMIC_REVIEWS,
    disabled: false,
  },
  {
    label: 'Urgency Boost',
    value: ExtensionComponentType.URGENCY_BOOST,
    disabled: false,
    dataInterceptor: (metricsData: CheckoutComponentMetric[]): CheckoutComponentMetric[] => {
      const impressions = metricsData.find((m) => m.METRIC_NAME === 'number_unique_impressions');
      const numberOrders = metricsData.find((m) => m.METRIC_NAME === 'number_orders');
      const snapshotDate = impressions?.LATEST_SNAPSHOT_DATE ?? null;

      const impressionsValue = impressions?.METRIC_TOTAL_VALUE ?? 0;
      const ordersValue = numberOrders?.METRIC_TOTAL_VALUE ?? 0;

      return [
        {
          METRIC_NAME: 'number_unique_impressions',
          METRIC_TOTAL_VALUE: impressionsValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'number_orders',
          METRIC_TOTAL_VALUE: ordersValue,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
        {
          METRIC_NAME: 'cvr',
          METRIC_TOTAL_VALUE: impressionsValue > 0 ? (ordersValue / impressionsValue) * 100 : 0,
          LATEST_SNAPSHOT_DATE: snapshotDate,
        },
      ];
    },
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
      },
      {
        title: 'Orders',
        field: 'number_orders',
      },
      {
        title: 'CVR %',
        field: 'cvr',
      },
    ],
  },
  {
    label: 'Multi shipment',
    value: ExtensionComponentType.MULTI_SHIPMENT,
    disabled: false,
  },
  {
    label: 'OrderProtection.com',
    value: ExtensionComponentType.ORDER_PROTECTION,
    disabled: false,
    dataInterceptor: orderProtectionDataInterceptor,
    metrics: [
      {
        title: 'Impressions',
        field: 'number_unique_impressions',
        tooltip: 'How many customers saw Order Protection at checkout',
      },
      {
        title: '# Orders with Protection',
        field: 'attributed_orders',
        tooltip: 'Orders where customers kept or added protection',
      },
      {
        title: '% Usage Rate',
        field: 'usage_rate',
        tooltip: '% of customers who saw protection and ordered with it',
      },
      {
        title: 'Revenue',
        field: 'revenue',
        displayCurrency: true,
        tooltip: 'Revenue from Order Protection fees',
      },
    ],
  },
  {
    label: 'Dynamic Extension',
    value: ExtensionComponentType.DYNAMIC_EXTENSION,
    disabled: false,
  },
  {
    label: 'PDQ Cash back',
    value: ExtensionComponentType.HOOXPAY,
    disabled: false,
    metrics: [
      {
        title: 'Requests',
        field: 'api_calls',
      },
      {
        title: 'Offers',
        field: 'offers',
      },
      {
        title: 'Orders',
        field: 'card_provided',
      },
      {
        title: 'Activations',
        field: 'activations',
      },
    ],
  },
];
